import { useCallback } from "react";
import type { Column } from "@/components/DataTable/DataTable.types";
import { exportTableAsCSV, type TableExportMetadata } from "@/utils/tableExport";

export interface UseTableExportOptions<T> {
  /** Column definitions for the table */
  columns: Column<T>[];
  /** Data rows to export */
  data: T[];
  /** Export metadata */
  metadata?: TableExportMetadata;
}

export interface UseTableExportReturn {
  /** Trigger CSV export of the table data */
  exportCSV: () => Promise<void>;
  /** Whether export is available (has data and exportable columns) */
  canExport: boolean;
}

/**
 * Hook for exporting DataTable data as CSV.
 * Provides a stable callback for triggering exports with memoized dependencies.
 *
 * For client-side DataTables where all data is in the browser.
 * For server-side exports, use the `onExport` callback pattern instead.
 *
 * @param options - Configuration for table export
 * @returns Object with export function and status
 *
 * @example
 * ```tsx
 * const { exportCSV, canExport } = useTableExport({
 *   columns: deviceColumns,
 *   data: devices,
 *   metadata: {
 *     filename: "devices",
 *     timestamp: true
 *   }
 * });
 *
 * // In your DataControls actions
 * <DataControls
 *   actions={{
 *     items: [
 *       {
 *         id: "export",
 *         label: "Export CSV",
 *         icon: "Export",
 *         onClick: exportCSV,
 *         disabled: !canExport
 *       }
 *     ]
 *   }}
 * />
 * ```
 */
export function useTableExport<T>({ columns, data, metadata }: UseTableExportOptions<T>): UseTableExportReturn {
  const exportableColumns = columns.filter((col) => col.exportable !== false);
  const canExport = exportableColumns.length > 0 && data.length > 0;

  const exportCSV = useCallback(async () => {
    try {
      await exportTableAsCSV({ columns, data, metadata });
    } catch (error: unknown) {
      console.error("Table export failed:", error);
      throw error;
    }
  }, [columns, data, metadata]);

  return {
    exportCSV,
    canExport,
  };
}
